import { useEffect, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import {
  Avatar,
  Badge,
  Container,
  Divider,
  ListItemButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import DrawerMenuIcon from './DrawerMenuIcon';
import { updateDrawerMenu, UserType } from './drawerMenuSlice';
import LanguageSelector from '../../../app/layout/LanguageSelector';
import { ShoppingCart } from '@mui/icons-material';
import { logout } from '../../account/accountSlice';
import { stringToColor } from '../../../common/helpers/string-utils';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import {
  fetchDrawerRestaurantConfigAsync,
  resetDrawerSelectedRestaurant,
} from './drawerRestaurantSlice';
import { userFullName } from '../../../app/data/models/user';

import SeoUpdater from '../../../app/components/seo/SeoUpdater';

const DrawerMenu = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { menuItems, isCustomerPage, userType } = useAppSelector(
    (state) => state.drawerMenu
  );
  const { selectedRestaurant } = useAppSelector(
    (state) => state.drawerRestaurant
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const { cart } = useAppSelector((state) => state.cart);
  const { user } = useAppSelector((state) => state.account);
  const [drawerWidth, setDrawerWidth] = useState(0);

  const { restaurantId } = useParams<{ restaurantId: string }>();
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    const handleUrlChange = () => {
      const newPath = location.pathname;
      dispatch(updateDrawerMenu(newPath));
    };

    handleUrlChange(); // Initial check on mount

    return () => {
      // unlisten(); // Clean up the listener on unmount
    };
  }, [location, dispatch]);

  useEffect(() => {
    // restaurantId is now a GUID (string), so check for non-empty string
    const isDrawerVisible = menuItems.length > 0 && !!restaurantId;
    setIsDrawerVisible(isDrawerVisible);
    setDrawerWidth(isDrawerVisible ? 240 : 0);
  }, [menuItems, restaurantId]);

  // Fetch restaurant details if restaurantId is available
  useEffect(() => {
    if (restaurantId) {
      dispatch(fetchDrawerRestaurantConfigAsync(restaurantId));
    } else {
      dispatch(resetDrawerSelectedRestaurant());
    }
  }, [restaurantId, dispatch]);

  // User menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const userMenuOpen = Boolean(anchorEl);

  // Handle opening user menu
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle closing user menu
  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  // Navigation handlers
  const handleProfileNavigation = () => {
    navigate('/account/profile');
    handleUserMenuClose();
    if (mobileOpen) handleDrawerToggle();
  };

  const handleLogout = () => {
    handleUserMenuClose();
    dispatch(logout());
  };

  const getLogoLink = () => {
    if (restaurantId) {
      switch (userType) {
        case UserType.RestaurantManager:
          return '/manager/restaurants';
        case UserType.KitchenStaff:
          return '/kitchen-staff/restaurants';
        case UserType.RestaurantWaiter:
          return '/restaurant-waiter/restaurants';
        case UserType.Customer:
          return `/customer/restaurants/${restaurantId}`;
        default:
          return '/';
      }
    }
    if (isCustomerPage) {
      return '/customer';
    }
    return '/';
  };

  const drawer = (
    <Box>
      {selectedRestaurant ? (
        <Toolbar
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            py: 3,
            background: 'linear-gradient(145deg, #f0f0f0 0%, #e0e0e0 100%)',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          }}
        >
          <Box
            component="img"
            src={selectedRestaurant.logoUrl}
            alt={selectedRestaurant.name}
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2,
              objectFit: 'contain',
              m: 1,
              border: '1px solid',
              borderColor: 'grey.100',
              p: 0.5,
              bgcolor: 'white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
            }}
          />
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              fontWeight: 600,
              color: 'black',
              textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
            }}
          >
            {selectedRestaurant.name}
          </Typography>
        </Toolbar>
      ) : (
        <Toolbar />
      )}

      <Divider />

      <List
        sx={{
          py: 0,
          '& .MuiListItemButton-root': {
            transition: 'all 0.3s ease',
            borderRadius: 2,
            my: 0.5,
            mx: 1,
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.05)',
              transform: 'translateX(10px)',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            },
            '&.Mui-selected': {
              backgroundColor: 'primary.light',
              color: 'primary.contrastText',
              '& .MuiListItemIcon-root': {
                color: 'primary.contrastText',
              },
              '&:hover': {
                backgroundColor: 'primary.light',
              },
            },
          },
        }}
      >
        {menuItems.map((item) => (
          <ListItemButton
            key={item.title}
            onClick={() => {
              navigate(item.path);
              if (mobileOpen) handleDrawerToggle();
            }}
            selected={location.pathname === item.path}
            sx={{
              px: 3,
              py: 1.5,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 48,
                color: 'text.secondary',
                transition: 'color 0.3s ease',
              }}
            >
              <DrawerMenuIcon iconType={item.icon} />
            </ListItemIcon>
            <ListItemText
              primary={t(item.title)}
              primaryTypographyProps={{
                sx: {
                  fontWeight: 500,
                  letterSpacing: '0.5px',
                },
              }}
            />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
      }}
    >
      <SeoUpdater />
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: 'white',
          color: 'primary.main',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {isDrawerVisible && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box
            component={Link}
            to={getLogoLink()}
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            {selectedRestaurant ? (
              <>
                <Box
                  component="img"
                  src={selectedRestaurant.logoUrl}
                  alt={selectedRestaurant.name}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    objectFit: 'contain',
                    m: 1,
                    border: '1px solid',
                    borderColor: 'grey.100',
                    p: 0.5,
                    bgcolor: 'white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                  }}
                />
                <Typography
                  variant="h6"
                  noWrap
                  component="div"
                  style={{ color: 'black' }}
                >
                  {selectedRestaurant.name}
                </Typography>
              </>
            ) : (
              <>
                <img
                  src="/logo.svg"
                  alt="SmartRisto Logo"
                  style={{
                    height: '40px',
                    width: '40px',
                    objectFit: 'contain',
                    marginRight: '12px',
                  }}
                />
                <Typography variant="h6" noWrap component="div">
                  {t('app.title')}
                </Typography>
              </>
            )}
          </Box>
          <Box display="flex" alignItems="center">
            {isCustomerPage && (
              <>
                <IconButton
                  component={Link}
                  to="cart"
                  size="large"
                  edge="start"
                  color="primary"
                  sx={{ mr: 1 }}
                >
                  <Badge badgeContent={cart?.totalQuantity} color="primary">
                    <ShoppingCart />
                  </Badge>
                </IconButton>
                <IconButton
                  component={Link}
                  to="orders"
                  size="large"
                  edge="start"
                  color="primary"
                  sx={{ mr: 1 }}
                >
                  <ReceiptLongIcon />
                </IconButton>
              </>
            )}
            <LanguageSelector />
            {user && (
              <>
                <IconButton onClick={handleUserMenuOpen}>
                  <Avatar
                    alt={`${user.firstName} ${user.lastName}`}
                    src={user.pictureUrl ?? ''}
                    sx={{
                      width: 44,
                      height: 44,
                      bgcolor: stringToColor(userFullName(user) || ''),
                      border: '2px solid',
                      borderColor: 'white',
                      boxShadow: '0 2px 3px rgba(0,0,0,0.2)',
                      cursor: 'pointer',
                      '& .MuiAvatar-img': {
                        objectFit: 'cover',
                        width: '100%',
                        height: '100%',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'white',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)',
                        fontSize: 22,
                        lineHeight: 1,
                      }}
                    >
                      {`${user.firstName[0]}${user.lastName[0]}`}
                    </Typography>
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={userMenuOpen}
                  onClose={handleUserMenuClose}
                >
                  <MenuItem onClick={handleProfileNavigation}>Profile</MenuItem>
                  <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { md: isDrawerVisible ? drawerWidth : 0 },
          flexShrink: { md: 0 },
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: isDrawerVisible ? 'block' : 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {restaurantId ? (
            drawer
          ) : (
            <Typography variant="body1" sx={{ p: 2 }}>
              Please select a restaurant to view the menu.
            </Typography>
          )}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: { xs: '56px', md: '64px' },
          pl: 2,
          pr: 2,
        }}
      >
        <Container maxWidth={false} disableGutters>
          {/* <AppBreadcrumb /> */}
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
};

export default DrawerMenu;
